import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonText,
} from '@ionic/react';
import { arrowBack, printOutline } from 'ionicons/icons';
import type { ColDef } from 'ag-grid-community';
import { reportingService, ReportRow } from '@/services/ReportingService';
import ModalFooter from '@/components/common/ModalFooter';

interface Props {
  isOpen: boolean;
  selectedRows: ReportRow[];
  columnDefs: ColDef[];
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const ExportPdfModal: React.FC<Props> = ({ isOpen, selectedRows, columnDefs, onClose, onSuccess }) => {
  const [filename, setFilename] = useState('report-export');
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    const validationError = reportingService.validateFilename(filename.trim());
    if (validationError) {
      setError(validationError);
      return;
    }

    setExporting(true);
    try {
      // Lazy import pdfmake to avoid SSR issues and keep bundle clean
      const pdfMake = (await import('pdfmake/build/pdfmake')).default;
      const pdfFonts = await import('pdfmake/build/vfs_fonts');
      (pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs ?? pdfFonts;

      // Build visible columns (skip private _attachments field)
      const visibleCols = columnDefs.filter(
        (col) => col.field && !col.field.startsWith('_') && col.headerName
      );

      const headers = visibleCols.map((col) => ({
        text: col.headerName ?? col.field ?? '',
        style: 'tableHeader',
        bold: true,
        fillColor: '#2d6db5',
        color: '#ffffff',
        fontSize: 9,
      }));

      const dataRows = selectedRows.map((row) =>
        visibleCols.map((col) => {
          const field = col.field as keyof ReportRow;
          const value = field ? (row as any)[field] : '';
          if (value === null || value === undefined) return { text: '', fontSize: 8 };
          if (typeof value === 'boolean') return { text: value ? 'Yes' : 'No', fontSize: 8 };
          return { text: String(value), fontSize: 8 };
        })
      );

      const docDefinition: any = {
        pageOrientation: 'landscape',
        pageMargins: [20, 30, 20, 30],
        content: [
          {
            text: filename.trim(),
            style: 'header',
            margin: [0, 0, 0, 10],
          },
          {
            text: `Generated: ${new Date().toLocaleString()}   |   Rows: ${selectedRows.length}`,
            style: 'subheader',
            margin: [0, 0, 0, 12],
          },
          {
            table: {
              headerRows: 1,
              widths: visibleCols.map(() => '*'),
              body: [headers, ...dataRows],
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#cccccc',
              vLineColor: () => '#cccccc',
              fillColor: (rowIndex: number) =>
                rowIndex === 0 ? null : rowIndex % 2 === 0 ? '#f5f5f5' : null,
            },
          },
        ],
        styles: {
          header: { fontSize: 14, bold: true, color: '#2d6db5' },
          subheader: { fontSize: 9, color: '#666666' },
          tableHeader: {},
        },
        defaultStyle: { font: 'Roboto' },
      };

      pdfMake.createPdf(docDefinition).download(`${filename.trim()}.pdf`);
      onSuccess(`PDF exported as "${filename.trim()}.pdf"`);
      onClose();
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose} style={{ '--width': '420px', '--height': '65vh' }}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Export to PDF</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* File name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#444' }}>
              File Name
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="text"
                value={filename}
                onChange={(e) => { setFilename(e.target.value); setError(null); }}
                placeholder="report-export"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  fontSize: '0.95rem',
                  border: error ? '1px solid var(--ion-color-danger)' : '1px solid #ccc',
                  borderRadius: '6px',
                  outline: 'none',
                  background: '#fff',
                  color: '#333',
                }}
              />
              <span style={{ fontSize: '0.9rem', color: '#666', whiteSpace: 'nowrap' }}>.pdf</span>
            </div>
            {error && (
              <IonText color="danger" style={{ fontSize: '0.8rem' }}>{error}</IonText>
            )}
          </div>

          <IonText color="medium" style={{ fontSize: '0.78rem' }}>
            Selected rows ({selectedRows.length}) will be exported in landscape layout.
            Filename may contain letters, numbers, spaces, hyphens, underscores, and periods.
          </IonText>
        </div>
      </IonContent>

      <ModalFooter
        cancelLabel="Cancel"
        actionLabel={exporting ? 'Generating...' : 'Export PDF'}
        cancelIcon={arrowBack}
        actionIcon={printOutline}
        onCancel={handleClose}
        onAction={handleExport}
        isCancelDisabled={exporting}
        isActionDisabled={exporting}
        isActionLoading={exporting}
        cancelFill="outline"
      />
    </IonModal>
  );
};

export default ExportPdfModal;
